import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { FolderDetail } from '@persped/shared';
import { useState } from 'react';
import { ApiError } from '../../api/ApiError';
import { useUpdateFolderText } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

const MAX_LENGTH = 4000;

/**
 * Gerincszöveg szerkesztése — a spec szerint WEB-PRIMÉR munkafolyamat
 * (US-FOLDER-4: „azonnal és később is szerkeszthető").
 *
 * A `rowVersion` a BETÖLTÖTT adatlapról megy vissza. Ha közben más
 * módosította, a szerver `CONCURRENT_MODIFICATION`-t ad, és NEM ír felül
 * némán (spec 15.5 §: „Konkurens módosításnál a rendszer ne írjon felül
 * észrevétlenül frissebb adatot"). Ilyenkor NEM próbáljuk automatikusan
 * összefésülni — a felhasználónak látnia kell, hogy mi változott, mielőtt
 * dönt. A felület ezért újratöltést kínál, nem újrapróbálást.
 *
 * A komponenst a szülő `key`-jel teremti újra, ezért a helyi állapot mindig
 * a friss szerveradatból indul — effektbeli setState nélkül.
 */
export function EditTextCard({
  folder,
  onReload,
}: {
  folder: FolderDetail;
  onReload: () => void;
}) {
  const update = useUpdateFolderText();
  const [text, setText] = useState(folder.currentText ?? '');
  const [approved, setApproved] = useState(folder.isTextApproved);

  const changed = text !== (folder.currentText ?? '') || approved !== folder.isTextApproved;
  const tooLong = text.length > MAX_LENGTH;

  const conflict =
    update.error instanceof ApiError && update.error.code === 'CONCURRENT_MODIFICATION';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">Gerincszöveg szerkesztése</Typography>

        {conflict ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={onReload}>
                Újratöltés
              </Button>
            }
          >
            <AlertTitle>Valaki más közben módosította</AlertTitle>
            A mentés nem történt meg, hogy ne írjuk felül a frissebb adatot. Töltsd újra az
            adatlapot, nézd meg a jelenlegi szöveget, és utána javítsd újra.
          </Alert>
        ) : (
          update.isError && <ProblemAlert error={update.error} />
        )}

        {update.isSuccess && !changed && <Alert severity="success">Elmentve.</Alert>}

        <TextField
          label="Gerincszöveg"
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={4}
          fullWidth
          error={tooLong}
          helperText={`${text.length} / ${MAX_LENGTH} karakter`}
        />

        <FormControlLabel
          control={
            <Checkbox checked={approved} onChange={(e) => setApproved(e.target.checked)} />
          }
          label="A szöveget ellenőriztem és jóváhagyom"
        />

        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Button
            variant="contained"
            disabled={!changed || tooLong || update.isPending}
            onClick={() =>
              update.mutate({
                barcode: folder.barcode,
                text,
                rowVersion: folder.rowVersion,
                isApproved: approved,
              })
            }
          >
            Mentés
          </Button>
          <Button
            disabled={!changed || update.isPending}
            onClick={() => {
              setText(folder.currentText ?? '');
              setApproved(folder.isTextApproved);
              update.reset();
            }}
          >
            Elvetés
          </Button>
        </Stack>

        {/*
          A 13. alapelv: a felismerési eredmény SOHA nem írja a currentText-et.
          Egyetlen írója van, ez az emberi művelet — ezért mondjuk ki itt is.
        */}
        <Typography variant="caption" color="text.secondary">
          A felismerés eredménye sosem írja felül automatikusan ezt a szöveget — az itt mentett
          érték az egyetlen hiteles gerincszöveg.
        </Typography>
      </Stack>
    </Paper>
  );
}
